import { supabase, Restaurant } from '../lib/supabase'

// Interface for restaurant creation with user data
interface RestaurantCreationData {
  // User account details
  owner_name: string
  owner_password: string
  // Restaurant details
  name: string
  address_line1: string
  city: string
  state: string
  pin_code: string
  latitude?: number
  longitude?: number
  profile_image?: string
  contact_phone: string
  contact_email: string
  opening_hours: string
  closing_hours: string
  restaurant_types: string[]
  cuisines: string[]
  price_range: 'budget' | 'moderate' | 'expensive' | 'luxury'
  images: string[]
  amenities: string[]
  days_open: string[]
  status: string
  cost_for_two: number
  rating?: number
}

// Demo data for when Supabase is not configured
const demoRestaurants: Restaurant[] = [
  {
    restaurant_id: '1',
    name: 'Spice Garden',
    address_line1: '123 MG Road, Bangalore, Karnataka 560001',
    city: 'Bangalore',
    state: 'Karnataka',
    pin_code: '560001',
    latitude: 12.9716,
    longitude: 77.5946,
    owner_user_id: 'user_123',
    contact_email: 'rajesh@spicegarden.com',
    contact_phone: '+91-9876543210',
    opening_hours: '11:00',
    closing_hours: '23:00',
    restaurant_types: ['Fine Dining', 'Casual Dining'],
    cuisines: ['Indian', 'North Indian', 'Punjabi'],
    price_range: 'moderate',
    images: [],
    amenities: ['WiFi', 'Parking', 'AC'],
    days_open: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    status: 'pending',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    cost_for_two: 800,
    rating: 4.2
  },
  {
    restaurant_id: '2',
    name: 'Golden Dragon',
    address_line1: '456 Brigade Road, Bangalore, Karnataka 560025',
    city: 'Bangalore',
    state: 'Karnataka',
    pin_code: '560025',
    latitude: 12.9716,
    longitude: 77.5946,
    owner_user_id: 'user_456',
    contact_email: 'liwei@goldendragon.com',
    contact_phone: '+91-9876543211',
    opening_hours: '12:00',
    closing_hours: '22:30',
    restaurant_types: ['Casual Dining', 'Chinese'],
    cuisines: ['Chinese', 'Asian'],
    price_range: 'moderate',
    images: [],
    amenities: ['WiFi', 'Parking', 'AC', 'Outdoor Seating'],
    days_open: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    status: 'approved',
    created_at: new Date(Date.now() - 86400000).toISOString(),
    updated_at: new Date(Date.now() - 86400000).toISOString(),
    cost_for_two: 1200,
    rating: 4.5
  },
  {
    restaurant_id: '3',
    name: 'Bella Vista',
    address_line1: '789 Church Street, Bangalore, Karnataka 560001',
    city: 'Bangalore',
    state: 'Karnataka',
    pin_code: '560001',
    latitude: 12.9716,
    longitude: 77.5946,
    owner_user_id: 'user_789',
    contact_email: 'marco@bellavista.com',
    contact_phone: '+91-9876543212',
    opening_hours: '18:00',
    closing_hours: '23:00',
    restaurant_types: ['Fine Dining', 'Italian'],
    cuisines: ['Italian', 'Mediterranean'],
    price_range: 'expensive',
    images: [],
    amenities: ['WiFi', 'Parking', 'AC', 'Live Music', 'Bar'],
    days_open: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    status: 'rejected',
    created_at: new Date(Date.now() - 172800000).toISOString(),
    updated_at: new Date(Date.now() - 172800000).toISOString(),
    cost_for_two: 2500,
    rating: 4.8
  }
]

// Check if we're in demo mode
const isDemoMode = !process.env.REACT_APP_SUPABASE_URL || 
                   process.env.REACT_APP_SUPABASE_URL === 'https://placeholder.supabase.co'

export const restaurantService = {
  // Get all restaurants
  async getAllRestaurants(): Promise<Restaurant[]> {
    if (isDemoMode) {
      // Return demo data with a small delay to simulate API call
      await new Promise(resolve => setTimeout(resolve, 500))
      return [...demoRestaurants]
    }

    const { data, error } = await supabase
      .from('restaurants')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      throw error
    }

    return data || []
  },

  // Create a new restaurant with user account
  async createRestaurant(restaurantData: RestaurantCreationData): Promise<Restaurant> {
    if (isDemoMode) {
      // In demo mode, simulate user creation and restaurant creation
      
      const newRestaurant: Restaurant = {
        restaurant_id: Date.now().toString(),
        name: restaurantData.name,
        address_line1: restaurantData.address_line1,
        city: restaurantData.city,
        state: restaurantData.state,
        pin_code: restaurantData.pin_code,
        latitude: restaurantData.latitude,
        longitude: restaurantData.longitude,
        contact_phone: restaurantData.contact_phone,
        contact_email: restaurantData.contact_email,
        opening_hours: restaurantData.opening_hours,
        closing_hours: restaurantData.closing_hours,
        restaurant_types: restaurantData.restaurant_types,
        cuisines: restaurantData.cuisines,
        price_range: restaurantData.price_range,
        images: restaurantData.images,
        // profile_image: restaurantData.profile_image, // Temporarily commented out until column is added
        amenities: restaurantData.amenities,
        days_open: restaurantData.days_open,
        status: restaurantData.status as Restaurant['status'],
        owner_user_id: `demo-user-${Date.now()}`, // Simulated user ID
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        cost_for_two: restaurantData.cost_for_two,
        rating: restaurantData.rating || 0
      }
      
      demoRestaurants.unshift(newRestaurant)
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate network delay
      return newRestaurant
    }

    try {
      let userId: string | null = null
      
      // Step 1: Try to create user account, handle existing user case
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: restaurantData.contact_email,
        password: restaurantData.owner_password,
        options: {
          data: {
            full_name: restaurantData.owner_name,
            phone: restaurantData.contact_phone,
            role: 'restaurant_owner'
          }
        }
      })

      if (authError) {
        // If user already exists, we need to handle this differently
        if (authError.message.includes('already registered') || authError.message.includes('already been registered') || authError.status === 422) {
          // For existing users, we'll create the restaurant without linking to user
          // This is a temporary solution - in production you'd want to handle this differently
          userId = null // Will be set to null in database
        } else {
          throw new Error(`Failed to create user account: ${authError.message}`)
        }
      } else {
        if (!authData.user) {
          throw new Error('User creation failed: No user data returned')
        }
        userId = authData.user.id
      }

      // Step 2: Create restaurant record linked to the user (if user was created)
      const { data: restaurantDbData, error: restaurantError } = await supabase
        .from('restaurants')
        .insert([{
          name: restaurantData.name,
          owner_user_id: userId, // Link to the user (new or existing), null if user creation failed
          address_line1: restaurantData.address_line1,
          city: restaurantData.city,
          state: restaurantData.state,
          pin_code: restaurantData.pin_code,
          latitude: restaurantData.latitude,
          longitude: restaurantData.longitude,
          contact_phone: restaurantData.contact_phone,
          contact_email: restaurantData.contact_email,
          opening_hours: restaurantData.opening_hours,
          closing_hours: restaurantData.closing_hours,
          restaurant_types: restaurantData.restaurant_types,
          cuisines: restaurantData.cuisines,
          price_range: restaurantData.price_range,
          images: restaurantData.images,
          // profile_image: restaurantData.profile_image, // Temporarily commented out until column is added
          amenities: restaurantData.amenities,
          days_open: restaurantData.days_open,
          status: restaurantData.status as Restaurant['status'],
          cost_for_two: restaurantData.cost_for_two,
          rating: restaurantData.rating
        }])
        .select()
        .single()

      if (restaurantError) {
        // Specific handling for enum errors
        if (restaurantError.code === '22P02' && restaurantError.message.includes('price_range')) {
          throw new Error(`Database setup incomplete: The price_range enum type is missing. Please run the database-setup.sql script in your Supabase SQL editor.`)
        }
        
        if (restaurantError.code === '22P02' && restaurantError.message.includes('restaurant_status')) {
          throw new Error(`Database setup incomplete: The restaurant_status enum type is missing. Please run the database-setup.sql script in your Supabase SQL editor.`)
        }
        
        // If restaurant creation fails, we should ideally clean up the user account
        // For now, we'll just throw the error with more details
        throw new Error(`Failed to create restaurant: ${restaurantError.message}. Details: ${JSON.stringify(restaurantError)}`)
      }

      return restaurantDbData

    } catch (error) {
      throw error
    }
  },

  // Update restaurant status
  async updateRestaurantStatus(id: string, status: Restaurant['status']): Promise<Restaurant> {
    if (isDemoMode) {
      // Update demo data
      const restaurant = demoRestaurants.find(r => r.restaurant_id === id)
      if (restaurant) {
        restaurant.status = status
        restaurant.updated_at = new Date().toISOString()
        await new Promise(resolve => setTimeout(resolve, 500))
        return restaurant
      }
      throw new Error('Restaurant not found')
    }

    const { data, error } = await supabase
      .from('restaurants')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('restaurant_id', id)
      .select()
      .single()

    if (error) {
      throw error
    }

    return data
  },

  // Get restaurant by ID
  async getRestaurantById(id: string): Promise<Restaurant | null> {
    if (isDemoMode) {
      return demoRestaurants.find(r => r.restaurant_id === id) || null
    }

    const { data, error } = await supabase
      .from('restaurants')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      return null
    }

    return data
  },

  // Update restaurant vibe_id
  async updateRestaurantVibes(id: string, vibe_id: string[]): Promise<Restaurant> {
    if (isDemoMode) {
      // Update demo data
      const restaurant = demoRestaurants.find(r => r.restaurant_id === id)
      if (restaurant) {
        restaurant.vibe_id = vibe_id
        restaurant.updated_at = new Date().toISOString()
        await new Promise(resolve => setTimeout(resolve, 500))
        return restaurant
      }
      throw new Error('Restaurant not found')
    }

    const { data, error } = await supabase
      .from('restaurants')
      .update({ 
        vibe_id,
        updated_at: new Date().toISOString()
      })
      .eq('restaurant_id', id)
      .select()
      .single()

    if (error) {
      throw error
    }

    return data
  }
}